import dayjs from "dayjs";

export const cloneDeep = function (obj: any): any {
  return JSON.parse(JSON.stringify(obj));
};

export const parseFlexibleDate = (
  date: string | number | Date | null | undefined
): dayjs.Dayjs | null | undefined => {
  if (date == null) {
    return date;
  }

  if (date instanceof Date) {
    return dayjs(date);
  }

  if (typeof date === "string" && /^\d+$/.test(date)) {
    return dayjs(parseInt(date, 10));
  }

  return dayjs(date);
};

/**
 * Type for a debounced function with cancel method
 */
export type DebouncedFunction<T extends (...args: any[]) => any> = T & {
  cancel: () => void;
};

/**
 * Simple debounce implementation
 * Delays function execution until after a specified wait time has passed
 * since the last time it was invoked
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns Debounced function with cancel method
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): DebouncedFunction<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as DebouncedFunction<T>;
}
